<template>
  <div class="padding-bottom-xs">
    <div class="product-details-container small-margin-top-z medium-margin-top-xl padding-top-xs">
      <h2 class="h4 small-margin-bottom-s xlarge-margin-bottom-m">Product Details</h2>
      <div class="product-details-info">
        <!--
        a11y rules disabled: wrapper isn't interactive;
        @click used only as overlay click workaround.
      -->
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
        <div :class="[oneColumnLayout ? 'one-column-layout' : 'two-column-layout', { clickable: isOverflowing }]" @click="handleWrapperClick">
          <div class="product-details-content-wrapper">
            <div
              ref="detailsContent"
              class="details-content columns-amount"
              :class="{
                'expanded-mobile': isExpandedMobile && mobileDevices,
                'has-low-highlights-amount': hasLowHighlightsAmount,
                'has-middle-highlights-amount': hasMiddleHighlightsAmount,
              }"
              :style="{ maxHeight: highlightsHeight ? `${highlightsHeight}px` : null }"
            >
              <ul>
                <li v-if="productDescription" data-auto="product-long-description-section" class="long-description">
                  <div v-html="productDescription"></div>
                </li>
                <li v-if="isSummaryAvailable" class="column" data-auto="product-summary-section">
                  <h4 class="heavy">Features</h4>
                  <Summary :open-as-initial-slideout="true" />
                </li>

                <li v-if="isSizeAndFitAvailable" class="column" data-auto="size-fit-section">
                  <h4 class="heavy">Size & Fit</h4>
                  <SizeAndFit />
                </li>

                <li v-if="isIngredientsAvailable" class="column" data-auto="product-dimensions-section">
                  <h4 class="heavy">
                    {{ dimensionsTitle }}
                  </h4>
                  <Ingredients />
                </li>

                <li v-if="isDimensionsAvailable" class="column" data-auto="product-dimensions-section">
                  <h4 class="heavy">Product Dimensions</h4>
                  <Dimensions />
                </li>

                <li v-if="isMaterialAndCareAvailable" class="column" data-auto="materials-care-section">
                  <h4 class="heavy">Materials & Care</h4>
                  <MaterialsAndCare :open-as-initial-slideout="true" />
                </li>

                <li v-if="isInstructionsAvailable" class="column" data-auto="instructions-section">
                  <h4 class="heavy">Instructions</h4>
                  <Instructions />
                </li>

                <li v-if="isTermsAndConditionsAvailable" class="column" data-auto="terms-conditions-section">
                  <h4 class="heavy">Terms & Conditions</h4>
                  <TermsAndConditions />
                </li>

                <li v-if="isShippingAndReturnsAvailable" class="column" data-auto="shipping-returns-section">
                  <h4 class="heavy">Shipping & Returns</h4>
                  <ShippingAndReturns />
                </li>

                <li v-if="isWhiteGloveDeliveryAvailable" class="column" data-auto="white-glove-delivery-section">
                  <h4 class="heavy">
                    {{ whiteGloveTitle }}
                  </h4>
                  <WhiteGloveDelivery />
                </li>
                <li ref="genesysChatContainer" class="column" data-auto="genesys-chat-widget-section">
                  <div id="rnowChatLinkContainer_type16" :class="{ 'genesys-fine-jewelry': isFineJewelryCategory }"></div>
                </li>
                <li v-if="productWebId">
                  <span class="product-id"
                    >Web ID:
                    {{ productWebId }}
                  </span>
                </li>
              </ul>
            </div>
            <div v-if="isOverflowing && !(isExpandedMobile && mobileDevices)" class="fade-out-overlay"></div>
          </div>
        </div>
        <div v-if="shouldShowHighlights" class="product-highlights-section">
          <ProductHighlights
            :key="highlightsKey"
            :highlights="productHighlights"
            :has-low-highlights-amount="hasLowHighlightsAmount"
            :has-middle-highlights-amount="hasMiddleHighlightsAmount"
            @height-change="handleHighlightsHeightUpdate"
          />
        </div>
      </div>
    </div>
    <div class="product-details-buttons">
      <Button v-if="showButton" variant="pill-button" class="view-full-details margin-top-s" @click="handleButtonClick">
        <span class="heavy">{{ buttonText }}</span>
      </Button>
    </div>
  </div>
</template>

<script setup>
import { computed, toValue, ref, onMounted, watch, nextTick, defineAsyncComponent } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductType } from '~/composables/useProductType';
import { useBrand } from '~/composables/useBrand';
import { useSlideout } from '~/composables/utils/useSlideout';
import tagViewMoreViewLessToggle from '~/features/analytics/productDetails';
import { useProductDetailsProvide, useProductDetails } from '../composables/useProductDetails';
import { useProductDetailsAvailabilityProvide, useProductDetailsAvailability } from '../composables/useProductDetailsAvailability';
import { storeToRefs } from 'pinia';

import Summary from './SummaryView.vue';
import Dimensions from './DimensionsView.vue';
import MaterialsAndCare from './MaterialsAndCare.vue';
import Instructions from './InstructionsView.vue';
import TermsAndConditions from './TermsAndConditions.vue';
import SizeAndFit from './SizeAndFit.vue';
import WhiteGloveDelivery from './WhiteGloveDelivery.vue';
import ShippingAndReturns from './ShippingAndReturns.vue';
import Ingredients from './IngredientsView.vue';
import ProductHighlights from './ProductHighlights.vue';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility.js';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import Button from '@atomic-ui/button/Button';
import { useDivisionCategoryFlags } from '~/modules/featureControl/composables/useDivisionCategoryFlags';

useProductDetailsProvide();
useProductDetailsAvailabilityProvide();

const { productId, productData: product } = useProduct();
const { isFineJewelryCategory } = useDivisionCategoryFlags();
const { isConsolidatedPage } = useProductType();
const { bigTicketDeliveryMethods } = useCommonProductGetters();
const { productDescription, dimensionsTitle, highlights: productHighlights } = useProductDetails();
const { isMcom } = useBrand();
const { showSlideout } = useSlideout();
const { isShopTheSetExpEnabled } = useFeatureEligibility();
const productDetailsText = isMcom ? 'Product details' : 'Product Details';
const whiteGloveTitle = computed(() => (toValue(bigTicketDeliveryMethods)?.length ? 'Delivery Methods' : 'White Glove Delivery'));
const { isMobile, isTablet } = useDeviceType();
const { selectedMember } = storeToRefs(useConsolidatedMemberStore());

const {
  isSummaryAvailable,
  isSizeAndFitAvailable,
  isIngredientsAvailable,
  isDimensionsAvailable,
  isMaterialAndCareAvailable,
  isInstructionsAvailable,
  isTermsAndConditionsAvailable,
  isShippingAndReturnsAvailable,
  isWhiteGloveDeliveryAvailable,
  isHighlightsAvailable,
} = useProductDetailsAvailability();

const shouldShowHighlights = computed(() => isHighlightsAvailable.value && (toValue(productHighlights)?.length ?? 0) > 0);

const hasLowHighlightsAmount = computed(() => (toValue(productHighlights)?.length ?? 0) < 5);
const hasMiddleHighlightsAmount = computed(() => (toValue(productHighlights)?.length ?? 0) > 4 && (toValue(productHighlights)?.length ?? 0) < 7);

const detailsContent = ref(null);
const genesysChatContainer = ref(null);
const highlightsHeight = ref(null);
const isOverflowing = ref(false);
const isExpandedMobile = ref(false);
const mobileDevices = computed(() => isMobile.value || isTablet.value);
const oneColumnLayout = computed(() => shouldShowHighlights.value || mobileDevices.value);
const productWebId = computed(() => (isShopTheSetExpEnabled ? selectedMember : productId));
const isConsolidatedOrShopTheSet = isShopTheSetExpEnabled || isConsolidatedPage;
const highlightsKey = computed(() => (isConsolidatedOrShopTheSet ? selectedMember?.value : toValue(productId)));

const showButton = computed(() => isOverflowing.value || (isExpandedMobile.value && mobileDevices.value));
const buttonText = computed(() => (isExpandedMobile.value && mobileDevices.value ? 'View less details' : 'View full details'));

const handleHighlightsHeightUpdate = (height) => {
  if (!mobileDevices.value) {
    highlightsHeight.value = height;
    // After the DOM updates with the new max-height, re-check for overflow.
    nextTick(checkOverflow);
  }
};

const checkOverflow = () => {
  const element = detailsContent.value;
  if (element) {
    // Use the explicit max-height from the highlights component if available,
    // otherwise fall back to the actual rendered height of the container.
    const containerHeight = highlightsHeight.value || element.clientHeight;
    const containerWidth = element.clientWidth;
    const contentHeight = element.scrollHeight;
    const contentWidth = element.scrollWidth;

    isOverflowing.value = contentHeight > containerHeight || contentWidth > containerWidth;
  }
};

useResizeObserver(detailsContent, () => {
  checkOverflow();
});

useResizeObserver(genesysChatContainer, () => {
  checkOverflow();
  if (!(isExpandedMobile.value && mobileDevices.value)) {
    blockKeyboardNavigation();
  }
});

const showProductDetailsDrawer = () => {
  showSlideout({
    id: 'product-details-drawer',
    title: productDetailsText,
    component: defineAsyncComponent(
      /* istanbul ignore next */ () => import(/* webpackChunkName: "ProductDetailsDrawer" */ './slideouts/ProductDetailsDrawer.vue'),
    ),
    backable: false,
    props: {
      productId,
      product,
      productWebId,
    },
  });
};

const toggleExpansionState = () => {
  if (mobileDevices.value) {
    isExpandedMobile.value = !isExpandedMobile.value;
    tagViewMoreViewLessToggle(isExpandedMobile.value);
  } else {
    showProductDetailsDrawer();
    tagViewMoreViewLessToggle(true);
  }
};

const handleButtonClick = () => {
  toggleExpansionState();
};

/** @param {MouseEvent} event */
const handleWrapperClick = (event) => {
  if (!isOverflowing.value) return;

  const { target } = event;
  if (target instanceof Element && target.closest('a, button')) return;

  toggleExpansionState();
};

const blockKeyboardNavigation = () => {
  if (!detailsContent.value) return;

  const disableFocusableElements = () => {
    if (!detailsContent.value) return;

    const FOCUSABLE_SELECTOR = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusables = detailsContent.value.querySelectorAll(FOCUSABLE_SELECTOR);
    focusables.forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  };

  disableFocusableElements();

  const mutationObserver = new MutationObserver(() => {
    disableFocusableElements();
    mutationObserver.disconnect();
  });

  mutationObserver.observe(detailsContent.value, {
    childList: true,
    subtree: true,
  });
};

const enableKeyboardNavigation = () => {
  if (!detailsContent.value) return;

  const FOCUSABLE_SELECTOR = '[tabindex="-1"]';
  const focusables = detailsContent.value.querySelectorAll(FOCUSABLE_SELECTOR);

  focusables.forEach((element) => {
    element.removeAttribute('tabindex');
  });
};

onMounted(() => {
  nextTick(checkOverflow);
  blockKeyboardNavigation();
});

watch(isExpandedMobile, (isExpanded) => {
  if (mobileDevices.value) {
    if (isExpanded) {
      enableKeyboardNavigation();
    } else {
      blockKeyboardNavigation();
    }
  }
});
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '~@core/csg/src/scss/components/pill-button-mixin.scss';
@include csg-pill-button;

.product-details-container {
  display: flex;
  flex-direction: column;
}

.product-details-info {
  display: flex;
  flex-direction: column-reverse;
}

@include breakpoint(large up) {
  .product-details-info {
    flex-direction: row;
  }
}

.clickable {
  cursor: pointer;
}

.product-id {
  color: $gray-2-color;
}

.product-details-content-wrapper {
  position: relative;
}

.two-column-layout {
  position: relative;

  .columns-amount {
    column-count: 2;

    @include breakpoint(small only) {
      column-count: 1;
    }
    @include breakpoint(medium only) {
      column-count: 1;
    }
  }
}

@include breakpoint(large up) {
  .two-column-layout {
    width: 84%;
  }
}

@include breakpoint(mlarge up) {
  .two-column-layout {
    width: 67%;
  }
}

.one-column-layout {
  position: relative;
  width: 25%;
}

.product-highlights-section {
  position: relative;
  width: 66%;
  height: 100%;
}

@include breakpoint(large only) {
  .one-column-layout {
    width: 33%;
  }

  .product-highlights-section {
    width: 50%;
  }
}

@include breakpoint(small only) {
  .one-column-layout {
    width: 100%;
  }

  .product-highlights-section {
    width: 100%;
  }
}
@include breakpoint(medium only) {
  .one-column-layout {
    width: 100%;
  }

  .product-highlights-section {
    width: 100%;
  }
}

@include breakpoint(small only) {
  .two-columns {
    column-count: 1;
  }
}

.details-content {
  column-gap: 2rem;
  column-fill: auto;
  max-height: rem-calc(255);
  overflow: hidden;
  list-style: none;
  padding: rem-calc(0 20 0 0);

  &.expanded-mobile {
    max-height: none;
    overflow: visible;
    display: grid;
  }

  li {
    list-style-type: none;
  }

  li:not(:last-child) {
    @include margin-bottom-s;
  }

  h4 {
    text-transform: capitalize;
  }

  ul {
    :deep(ul) {
      list-style-type: disc !important;
      padding-left: rem-calc(4);
    }
  }
}

.fade-out-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 5em; /* Adjust as needed */
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 99%);
  pointer-events: none; /* Allows clicks to pass through to the content */
}

.details-content.highlights-available {
  max-height: rem-calc(120); //set 110 for bcom
}

@include breakpoint(large only) {
  .details-content {
    max-height: rem-calc(375);
  }

  .details-content.has-low-highlights-amount {
    max-height: rem-calc(255);
  }

  .details-content.has-middle-highlights-amount {
    max-height: rem-calc(315);
  }
}

@include breakpoint(small only) {
  .two-columns {
    column-count: 1;
  }
}
@include breakpoint(medium only) {
  .two-columns {
    column-count: 1;
  }
}

.product-details-buttons {
  display: flex;
  width: 100%;
}

.view-details-btn {
  display: block;
  margin-top: rem-calc(24);
  padding: 0.5rem 1rem;
  border: 1px solid #000;
  background: #fff;
  cursor: pointer;
}

@include breakpoint(small only) {
  .long-description {
    @include margin-bottom-xs;
  }
  .details-content {
    column-count: 1;
  }
}

:deep(.salesfloor) {
  // Rewrite salesfloor styles
  margin-top: 0 !important;

  .sf-virtual-advisor--expanded {
    margin-top: 0 !important;
  }

  .sf-virtual-advisor--stylist-title {
    @include body(medium);
  }
}

.view-full-details {
  height: rem-calc(36);
}
</style>

<style lang="scss" brand="mcom" scoped>
.view-full-details {
  border-radius: rem-calc(50);
}
</style>
